import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { artProxyUrl } from '../artwork-texture.js';

// Shared-rig contract: forbidden. The wall is its own light source - a wash
// across it would raise the black the unlit cells are read against, and the
// off-cell grey is the one thing that must stay exactly where it is.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'hex',
  family: 'grid',
  danceability: 0.86,
  energyLevel: 0.84,
  bpmMin: 115,
  bpmMax: 180,
  movement: 0.70,
  tonality: 0.55,
  prefers: { gridStrength: [0.60, 1.0] },
  enabled: true
};

// Hex: a honeycomb wall of luminous cells, crossed by waves that travel from
// neighbour to neighbour.
//
// WHY A HEXAGONAL GRID AND NOT DISCOSQUARES. On a square grid a wave that
// advances one neighbour per step draws a diamond (Manhattan) or a square
// (Chebyshev). On a hexagonal grid every cell has six EQUIDISTANT neighbours,
// and the same neighbour-to-neighbour rule draws a near-perfect circle: the
// ring at distance k has a circumradius/apothem ratio of 1.155 against 1.414
// for the diamond. Rings, spirals and curved fronts are the natural shapes
// here and impossible there. That is the whole reason this piece exists, and
// `hexDistance` below is the one function everything else rests on.
//
// THE PROPAGATION IS ON THE GRID, NOT IN PIXELS. A wave is an integer front:
// at age k (in half-beats) exactly the cells at hexagonal distance k from the
// origin are lit, with a trail of a few cells behind. Nothing is interpolated
// across a cell. Interference is by ADDITION, like Cascade: where two fronts
// meet, the cell carries the sum and turns white.

// --- the wall ----------------------------------------------------------------
const CAMERA_Z = 10.0;
const OVERRUN = 1.15;            // the wall runs 15% past the frame on every side
const CURVE_RADIUS = 40.0;       // tier 2: the wall wraps around the viewer
const JOINT_PX = 1.0;            // per cell side - two cells make the 2 px joint
// COLS FOLLOW FROM THE WIDTH. The tier's column count is the FLOOR, not the
// value: the rows set the cell size (24 rows fill the height plus overrun), and
// at 16:9 that leaves 42 columns spanning 29% past the frame - fine. But the
// kiosk's own screens are 3.2:1, where 42 columns would end 40% short of the
// edges. So the width asks for as many columns as it needs, capped.
const COLS_CAP = 72;
const HEX_W = Math.sqrt(3);      // width of a pointy-top hex of circumradius 1
const ROW_PITCH = 1.5;           // vertical pitch, same unit

// --- light -------------------------------------------------------------------
// LINEAR values into an ACES + sRGB pipeline at exposure 1.1, MEASURED on the
// rendered frame rather than converted on paper: 0.016 came out at 10/255 and
// 0.024 at 18/255 (median of the unlit tiles, centre band, grain and vignette
// included), so 0.020 is the brief's 0.05 luminance. The first guess, 0.004,
// rendered as pure black - the wall did not exist. The peak is a LUMINANCE cap,
// so a lit cell blooms (threshold 0.78) and a white crossing blooms more.
const OFF_LUM = 0.020;
const PEAK_LUM = 0.92;
const SAFE_FLOOR = 0.45;
const CORE_GAIN = 0.45;          // the centre of a lit cell over its edge
const JOINT_GLOW = 0.28;         // the seam of a lit cell: the short halo
const SAT = 0.85;

// --- the waves ---------------------------------------------------------------
const TRAIL_DEFAULT = 3;
const TRAIL_MIN = 2;
const TRAIL_MAX = 5;
// Each trailing ring keeps this much of the one ahead - in LINEAR light. At
// 0.55 the three rings came out at 0.55/0.30/0.17, which the tone curve shows
// as 77%/58%/45%: a filled disc with a bright rim, not a trail. 0.33 gives
// 63%/38%/21% on screen, a front with a tail that visibly goes out.
const TRAIL_FALLOFF = 0.33;
const RING_ORIGIN_TRIES = 16;
// The six waves of BLOOM start this many cells from the centre, in the six
// axial directions. Eight half-beats is one bar, so fronts launched on a
// downbeat all reach the centre on the next one.
const BLOOM_DIST = 8;

// --- patterns ----------------------------------------------------------------
const PATTERNS = ['ring', 'spiral', 'rows', 'random', 'bloom', 'image'];
const SPIRAL_TAIL = 12;
const ROWS_DECAY = 1.4;          // per half-beat
const RANDOM_SHARE = 0.12;
const RANDOM_HOLD = 0.55;        // a random cell's level at the end of its beat
const IMAGE_CYCLE_BARS = 4;
// The whole wall as a picture is a FLOODLIGHT if every bright cell sits at the
// peak: measured at a frame mean of 212/255, white through the bloom. The
// picture is shown at this fraction of the level; the fronts that cross it
// still reach full and go white where they meet.
const IMAGE_GAIN = 0.50;
const SPARK_RATE = 6.0;          // off-cell sparks per second at bands.high = 1
const SPARK_LEVEL = 0.35;
const SPARK_DECAY = 9.0;         // per second

// --- events ------------------------------------------------------------------
// THE INVERSION IS NOT AT FULL LEVEL. The wall is mostly dark, so inverting it
// lights most of it; a whole wall at the peak for half a beat every bar would
// wash the frame white through the bloom (see MainStage's exposure traps). The
// level is the only thing tempered - the switch itself is instantaneous.
const INVERT_LEVEL = 0.55;
const INVERT_HALF_BEATS = 1;
const LIT_THRESHOLD = 0.2;       // above this a cell counts as lit for inversion
const DROP_FILL_HALF_BEATS = 2;  // the centre wave reaches the edges in one beat
const DROP_HOLD_HALF_BEATS = 8;  // then a bar of breathing
const DROP_FADE_HALF_BEATS = 2;

// --- the synthetic grid ------------------------------------------------------
// The same free-running clock Cascade uses when there is no beat grid: a radio
// stream, or the first seconds of a track. Beats, bars and phrases counted off
// it in the proportions a real grid has.
const SYNTH_BPM = 124;
const BEATS_PER_BAR = 4;
const BEATS_PER_PHRASE = 16;

const TIER = {
  0: { cols: 24, rows: 14, waves: 2, curve: false, image: false, patterns: ['ring', 'rows'] },
  1: { cols: 32, rows: 18, waves: 4, curve: false, image: false, patterns: ['ring', 'spiral', 'rows', 'random', 'bloom'] },
  2: { cols: 42, rows: 24, waves: 6, curve: true, image: true, patterns: PATTERNS }
};
const DEFAULT_TIER = 2;

const FALLBACK_VIVID = [0x36e0ff, 0xff5bd0, 0xffb02e, 0x7cff5a, 0xb46bff, 0xff4f4f];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

// =============================================================================
// THE FIELD - pure grid arithmetic, no THREE, no DOM. Exported so the
// acceptance criteria can be checked in Node.
// =============================================================================

// Axial directions of a pointy-top hex grid, in the order that walks a ring.
const DIRS = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]];

// Hexagonal distance in axial coordinates: the Manhattan norm of the cube
// coordinates, halved. This is the function that makes a ring a circle.
export function hexDistance(q1, r1, q2, r2) {
  const dq = q1 - q2, dr = r1 - r2;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

export class HexField {
  constructor(cols, rows, maxWaves) {
    this.cols = cols;
    this.rows = rows;
    this.n = cols * rows;
    this.maxWaves = maxWaves;
    // "odd-r" offset layout: odd rows are shifted half a cell to the right.
    // Stored in AXIAL coordinates, where the distance is a formula.
    this.q = new Int16Array(this.n);
    this.r = new Int16Array(this.n);
    this.col = new Int16Array(this.n);
    this.row = new Int16Array(this.n);
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const i = row * cols + col;
        this.col[i] = col;
        this.row[i] = row;
        this.q[i] = col - ((row - (row & 1)) >> 1);
        this.r[i] = row;
      }
    }
    this.centre = this.indexOf(cols >> 1, rows >> 1);
    this.centreDist = new Uint8Array(this.n);
    this.fillDistances(this.centreDist, this.centre);
    let maxD = 0;
    for (let i = 0; i < this.n; i++) if (this.centreDist[i] > maxD) maxD = this.centreDist[i];
    this.maxCentreDist = maxD;
    // The longest distance any origin can be from any cell - a wave older than
    // this plus its trail has left the grid.
    this.maxDist = hexDistance(this.q[0], this.r[0], this.q[this.n - 1], this.r[this.n - 1]) + rows;

    // the ring buffer of waves
    this.waveDist = new Uint8Array(maxWaves * this.n);
    this.waveBorn = new Float32Array(maxWaves);
    this.waveSpeed = new Float32Array(maxWaves);   // rings per half-beat
    this.waveActive = new Uint8Array(maxWaves);
    this.waveFill = new Uint8Array(maxWaves);      // 1: everything behind the front stays lit
    this.waveOrigin = new Int32Array(maxWaves);
    this.next = 0;

    this.spiral = this._spiralOrder();
  }

  indexOf(col, row) {
    if (col < 0 || row < 0 || col >= this.cols || row >= this.rows) return -1;
    return row * this.cols + col;
  }

  // Axial -> index, or -1 outside the grid.
  indexOfAxial(q, r) {
    return this.indexOf(q + ((r - (r & 1)) >> 1), r);
  }

  fillDistances(out, origin) {
    const oq = this.q[origin], or = this.r[origin];
    for (let i = 0; i < this.n; i++) {
      const d = hexDistance(this.q[i], this.r[i], oq, or);
      out[i] = d > 255 ? 255 : d;
    }
  }

  // The cells at exactly hexagonal distance k from the centre - a ring.
  ring(origin, k) {
    const out = [];
    const oq = this.q[origin], or = this.r[origin];
    for (let i = 0; i < this.n; i++) {
      if (hexDistance(this.q[i], this.r[i], oq, or) === k) out.push(i);
    }
    return out;
  }

  // Cell indices in spiral order from the centre: ring 1, ring 2, ... each
  // walked around. Cells that fall outside the rectangle are skipped, so the
  // snake keeps winding once it has passed the shorter side.
  _spiralOrder() {
    const out = [this.centre];
    const cq = this.q[this.centre], cr = this.r[this.centre];
    const maxRing = this.maxCentreDist;
    for (let k = 1; k <= maxRing; k++) {
      let q = cq + DIRS[4][0] * k, r = cr + DIRS[4][1] * k;
      for (let side = 0; side < 6; side++) {
        for (let step = 0; step < k; step++) {
          const i = this.indexOfAxial(q, r);
          if (i >= 0) out.push(i);
          q += DIRS[side][0]; r += DIRS[side][1];
        }
      }
    }
    return Int32Array.from(out);
  }

  // The cell at distance `dist` from the centre in axial direction `dir`,
  // pulled back inside the grid if the wall is too small in that direction.
  towards(dir, dist) {
    const d = DIRS[dir % 6];
    for (let k = dist; k >= 0; k--) {
      const i = this.indexOfAxial(this.q[this.centre] + d[0] * k, this.r[this.centre] + d[1] * k);
      if (i >= 0) return i;
    }
    return this.centre;
  }

  // A wave from `origin`, born at `bornHb` (half-beats). The distances are
  // computed ONCE here; evaluate() only compares them to the wave's age.
  launch(origin, bornHb, options) {
    const opts = options || {};
    const slot = this.next;
    this.next = (this.next + 1) % this.maxWaves;
    this.fillDistances(this.waveDist.subarray(slot * this.n, (slot + 1) * this.n), origin);
    this.waveBorn[slot] = bornHb;
    this.waveSpeed[slot] = opts.speed || 1;
    this.waveFill[slot] = opts.fill ? 1 : 0;
    this.waveOrigin[slot] = origin;
    this.waveActive[slot] = 1;
    return slot;
  }

  clear() { this.waveActive.fill(0); }

  activeCount() {
    let c = 0;
    for (let w = 0; w < this.maxWaves; w++) c += this.waveActive[w];
    return c;
  }

  // ADDS every active wave into `out` (which the caller has zeroed): the
  // front at 1.0, the trail behind it falling off ring by ring. Two fronts on
  // one cell sum to 2.0 - that is the interference.
  evaluate(hb, trail, out) {
    const n = this.n;
    for (let w = 0; w < this.maxWaves; w++) {
      if (!this.waveActive[w]) continue;
      const age = (hb - this.waveBorn[w]) * this.waveSpeed[w];
      if (age < 0) continue;
      const k = Math.floor(age);
      if (k > this.maxDist + trail) { this.waveActive[w] = 0; continue; }
      const dist = this.waveDist, base = w * n;
      if (this.waveFill[w]) {
        for (let i = 0; i < n; i++) if (dist[base + i] <= k) out[i] += 1;
        continue;
      }
      const lo = k - trail;
      for (let i = 0; i < n; i++) {
        const d = dist[base + i];
        if (d > k || d < lo) continue;
        out[i] += d === k ? 1 : Math.pow(TRAIL_FALLOFF, k - d);
      }
    }
  }
}

// =============================================================================
// SHADERS
// =============================================================================

// One InstancedMesh: a hexagon per cell, one float per cell per frame (aLit).
// lit in [0, 1] is the cell's level; anything above 1 is the WHITENESS of a
// crossing, so interference by addition needs no second attribute.
const VERTEX = `
  precision mediump float;
  attribute float aLit;
  attribute vec3 aColor;
  varying vec2 vP;
  varying float vLit;
  varying vec3 vColor;
  varying float vSafe;
${SAFE_ZONE_VERTEX_CHUNK}
  void main() {
    vP = position.xy;
    vLit = aLit;
    vColor = aColor;
    vec4 clip = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
    vSafe = safeZoneAttenuation(clip);
    gl_Position = clip;
  }
`;

const FRAGMENT = `
  precision mediump float;
  uniform float uJoint;      // seam width as a fraction of the apothem
  uniform float uPeak;
  uniform float uOff;
  varying vec2 vP;
  varying float vLit;
  varying vec3 vColor;
  varying float vSafe;
  void main() {
    // Pointy-top hexagon of circumradius 1: the edge normals sit at 0, 60 and
    // 120 degrees, so this is 1.0 exactly on the edge and 0.0 at the centre.
    vec2 a = abs(vP);
    float f = max(a.x, a.x * 0.5 + a.y * 0.8660254) / 0.8660254;
    float lit = min(vLit, 1.0);
    float white = clamp(vLit - 1.0, 0.0, 1.0);
    vec3 tint = mix(vColor, vec3(1.0), white);
    // A bright heart, falling to the edge - a lamp behind a diffuser.
    float core = 1.0 + ${CORE_GAIN.toFixed(3)} * (1.0 - f) * (1.0 - f);
    vec3 col = tint * lit * core;
    // Capped in LUMINANCE, so the cell blooms exactly as much as it should and
    // a white crossing blooms more than a coloured front.
    float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
    col *= lum > uPeak ? uPeak / lum : 1.0;
    // The wall exists even black: an unlit cell is a dark grey tile.
    col += vec3(uOff) * (1.0 - lit);
    // The seam: a dark joint of fixed pixel width. On a lit cell it glows a
    // little - that is the short halo, kept inside the cell so a single
    // opaque draw needs no blending and no ordering.
    float seam = smoothstep(1.0 - uJoint - 0.03, 1.0 - uJoint, f);
    col = mix(col, vec3(uOff * 0.3) + tint * lit * ${JOINT_GLOW.toFixed(3)}, seam);
    // Safe zone, floor ${SAFE_FLOOR.toFixed(2)}: remapped from the shared 0.32.
    float att = ${SAFE_FLOOR.toFixed(3)} + (vSafe - 0.32) * (${(1 - SAFE_FLOOR).toFixed(3)} / 0.68);
    gl_FragColor = vec4(col * att, 1.0);
  }
`;

// =============================================================================

export class HexVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._paused = false;
    this._beatClock = 0;       // continuous beats
    this._hb = 0;              // continuous half-beats - THE clock of the piece
    this._lastBeat = null;     // integer beat of the last event
    this._lastBarIndex = null;
    this._lastPhraseIndex = null;
    this._synthBeat = 0;
    this._beatCount = 0;
    this._barCount = 0;
    this._phraseCount = 0;
    this._invertUntil = -1;
    this._dropAt = -1e9;
    this._lastDropTime = -1e9;
    this._patternStartHb = 0;
    this._rowCursor = -1;
    this._bloomTurn = 0;
    this._lowBand = 0; this._highBand = 0;
    this._progress = 0;
    this._trail = TRAIL_DEFAULT;
    this._lastPalette = null;
    this._lastArtworkUrl = null;
    this._imageRGB = null;     // per cell, linear, normalised to max channel 1
    this._imageLevel = null;   // per cell max channel - the cell's own level
    this._colorMode = null;    // 'flat' | 'image'

    const forced = urlParam('hexmode');
    this._forcedPattern = forced && PATTERNS.indexOf(forced) >= 0 ? forced : null;
    this._pattern = this._forcedPattern || 'ring';
    this._forceWave = urlParam('hexwave') === '1';
  }

  // The frame at the wall's depth: what the camera shows at z = 0.
  _frame(camera, aspect) {
    const halfH = CAMERA_Z * Math.tan(camera.fov * Math.PI / 360);
    return { halfH: halfH, halfW: halfH * aspect };
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const L = this._layout;
    const self = this;

    camera.position.set(0, 0, CAMERA_Z);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();

    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    const fr = this._frame(camera, aspect);
    this._halfH = fr.halfH;
    // Rows set the cell size: (rows - 1) pitches plus one full cell fill the
    // height with its overrun. Columns then follow from the width (see
    // COLS_CAP), never fewer than the tier's own count.
    const R = (2 * fr.halfH * OVERRUN) / ((L.rows - 1) * ROW_PITCH + 2);
    this._R = R;
    const cellW = HEX_W * R;
    const cols = Math.min(COLS_CAP, Math.max(L.cols, Math.ceil((2 * fr.halfW * OVERRUN) / cellW) + 1));
    this._field = new HexField(cols, L.rows, L.waves);
    const n = this._field.n;
    this._n = n;

    // --- per-cell working arrays (allocated once; update() never allocates)
    this._sum = new Float32Array(n);
    this._extra = new Float32Array(n);
    this._spark = new Float32Array(n);
    this._randomLit = new Uint8Array(n);
    this._rowLitHb = new Float32Array(L.rows).fill(-1e9);
    this._xFlat = new Float32Array(n);
    this._y = new Float32Array(n);

    // --- geometry: a pointy-top hexagon of circumradius 1, six triangles
    const verts = new Float32Array(7 * 3);
    for (let k = 0; k < 6; k++) {
      const a = Math.PI / 6 + k * Math.PI / 3;
      verts[(k + 1) * 3] = Math.cos(a);
      verts[(k + 1) * 3 + 1] = Math.sin(a);
    }
    const index = [];
    for (let k = 0; k < 6; k++) index.push(0, k + 1, ((k + 1) % 6) + 1);
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
    geo.setIndex(index);
    this._litAttr = new THREE.InstancedBufferAttribute(new Float32Array(n), 1);
    this._litAttr.setUsage(THREE.DynamicDrawUsage);
    this._colorAttr = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
    this._colorAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aLit', this._litAttr);
    geo.setAttribute('aColor', this._colorAttr);
    this._geo = geo;

    this._uniforms = Object.assign({
      uJoint: { value: 0.05 },
      uPeak: { value: PEAK_LUM },
      uOff: { value: OFF_LUM }
    }, safeZoneUniforms);
    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      depthWrite: true,
      depthTest: true
    });

    this._mesh = new THREE.InstancedMesh(geo, this._material, n);
    this._mesh.instanceMatrix.setUsage(THREE.StaticDrawUsage);
    this._mesh.frustumCulled = false;
    this._mesh.renderOrder = -2000;

    // --- placement. Curved: the wall is a slice of a cylinder of radius
    // CURVE_RADIUS whose axis is on the camera's side, so the edges come
    // TOWARDS the viewer - a stage screen that wraps around the room.
    const halfCols = (cols - 1) * 0.5, halfRows = (L.rows - 1) * 0.5;
    const m = new THREE.Matrix4(), pos = new THREE.Vector3();
    const quat = new THREE.Quaternion(), scale = new THREE.Vector3(R, R, 1);
    const axisY = new THREE.Vector3(0, 1, 0);
    for (let i = 0; i < n; i++) {
      const col = this._field.col[i], row = this._field.row[i];
      const x = (col - halfCols + (row & 1) * 0.5 - 0.25) * cellW;
      const y = (row - halfRows) * ROW_PITCH * R;
      this._xFlat[i] = x;
      this._y[i] = y;
      if (L.curve) {
        const theta = x / CURVE_RADIUS;
        pos.set(CURVE_RADIUS * Math.sin(theta), y, CURVE_RADIUS * (1 - Math.cos(theta)));
        quat.setFromAxisAngle(axisY, -theta);
      } else {
        pos.set(x, y, 0);
        quat.identity();
      }
      m.compose(pos, quat, scale);
      this._mesh.setMatrixAt(i, m);
    }
    this._mesh.instanceMatrix.needsUpdate = true;

    // The seam is a PIXEL width, so it needs the real drawing-buffer height:
    // the apothem's size on screen is what the fraction is measured against.
    this._mesh.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(_size);
      const apothemPx = R * 0.8660254 * _size.y / (2 * self._halfH);
      self._uniforms.uJoint.value = Math.min(0.25, JOINT_PX / Math.max(1, apothemPx));
    };

    scene.add(this._mesh);

    this._setFlatColor(FALLBACK_VIVID[0]);
    this._litAttr.array.fill(0);
    this._litAttr.needsUpdate = true;
  }

  // --- colour ----------------------------------------------------------------

  // The phrase's colour: one of vividColors, saturation forced to SAT, with
  // enough lightness to have a body. Computed in sRGB (where the palette
  // lives) and converted to linear by hand - ColorManagement is off in this
  // build and Color.set() would hand the shader the raw sRGB value.
  _phraseColor(profile) {
    const palette = profile.artworkPalette;
    const vivid = palette && palette.vividColors && palette.vividColors.length
      ? palette.vividColors : FALLBACK_VIVID;
    return vivid[this._phraseCount % vivid.length];
  }

  _setFlatColor(hex) {
    _tmpColor.set(hex);
    _tmpColor.getHSL(_hsl);
    _tmpColor.setHSL(_hsl.h, SAT, Math.max(0.50, Math.min(0.62, _hsl.l)));
    const r = srgbToLinear(_tmpColor.r), g = srgbToLinear(_tmpColor.g), b = srgbToLinear(_tmpColor.b);
    const arr = this._colorAttr.array;
    for (let i = 0; i < this._n; i++) {
      arr[i * 3] = r; arr[i * 3 + 1] = g; arr[i * 3 + 2] = b;
    }
    this._colorAttr.needsUpdate = true;
    this._colorMode = 'flat';
    this._flatHex = hex;
  }

  _setImageColors() {
    if (!this._imageRGB) return;
    this._colorAttr.array.set(this._imageRGB);
    this._colorAttr.needsUpdate = true;
    this._colorMode = 'image';
  }

  // The cover, sampled on the grid: each cell takes the MEAN colour of its
  // own zone of the image - hexagonal pointillism. The cover is a square the
  // height of the visible frame, centred, and mirrored left and right so the
  // whole wall is used; the centre copy is the one you recognise. Same
  // crossOrigin + proxy route as artwork-palette.js, for the same CORS reason.
  _loadCover(url) {
    if (typeof Image === 'undefined' || typeof document === 'undefined') return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const self = this;
    img.onload = function () {
      if (self._lastArtworkUrl !== url || !self._field) return;
      try {
        const S = 96;
        const canvas = document.createElement('canvas');
        canvas.width = S; canvas.height = S;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, S, S);
        const data = ctx.getImageData(0, 0, S, S).data;
        self._sampleCover(data, S);
      } catch (err) {
        // A tainted canvas: no image this track. IMAGE then behaves as RING.
        self._imageRGB = null;
        self._imageLevel = null;
      }
    };
    img.onerror = function () { self._imageRGB = null; self._imageLevel = null; };
    img.src = artProxyUrl(url);
  }

  _sampleCover(data, S) {
    const n = this._n;
    const side = 2 * this._halfH;                 // the cover's side, world units
    const rgb = new Float32Array(n * 3);
    const level = new Float32Array(n);
    const cellPx = Math.max(1, Math.round(HEX_W * this._R / side * S));
    const half = cellPx >> 1;
    for (let i = 0; i < n; i++) {
      // mirror-tiled horizontally, clamped vertically
      let u = this._xFlat[i] / side + 0.5;
      u = u - 2 * Math.floor(u / 2);
      if (u > 1) u = 2 - u;
      const v = clamp01(0.5 - this._y[i] / side);
      const cx = Math.round(u * (S - 1)), cy = Math.round(v * (S - 1));
      let r = 0, g = 0, b = 0, c = 0;
      for (let dy = -half; dy <= half; dy++) {
        const yy = cy + dy;
        if (yy < 0 || yy >= S) continue;
        for (let dx = -half; dx <= half; dx++) {
          const xx = cx + dx;
          if (xx < 0 || xx >= S) continue;
          const p = (yy * S + xx) * 4;
          r += data[p]; g += data[p + 1]; b += data[p + 2]; c++;
        }
      }
      if (c === 0) c = 1;
      const lr = srgbToLinear(r / c / 255), lg = srgbToLinear(g / c / 255), lb = srgbToLinear(b / c / 255);
      const mx = Math.max(lr, lg, lb, 1e-4);
      // The colour carries the hue, the level carries the brightness: a wave
      // crossing the cover lights each cell at its own colour, full.
      rgb[i * 3] = lr / mx; rgb[i * 3 + 1] = lg / mx; rgb[i * 3 + 2] = lb / mx;
      level[i] = Math.min(1, Math.pow(mx, 0.9)) * IMAGE_GAIN;
    }
    this._imageRGB = rgb;
    this._imageLevel = level;
    if (this._pattern === 'image') this._setImageColors();
  }

  // --- the grid clock ----------------------------------------------------------

  // Beats, bars and phrases as EVENTS, from a grid when there is one and from
  // the synthetic clock otherwise. Returns nothing; sets this._hb and fires
  // _onBeat/_onBar/_onPhrase.
  _advanceClock(dt, profile, bpm) {
    const hasGrid = profile.beatIndex != null && profile.beatPhase != null;
    let beatClock;
    if (hasGrid) {
      beatClock = profile.beatIndex + profile.beatPhase;
      // A seek or a track change moves the grid under us: resync rather than
      // replaying every missed beat.
      if (this._lastBeat != null && Math.abs(beatClock - this._beatClock) > 4) this._resync();
    } else {
      this._synthBeat += dt * bpm / 60;
      beatClock = this._synthBeat;
    }
    this._beatClock = beatClock;
    this._hb = beatClock * 2;

    const beat = Math.floor(beatClock);
    if (this._lastBeat == null) { this._lastBeat = beat; return; }
    while (this._lastBeat < beat) {
      this._lastBeat++;
      this._beatCount++;
      this._onBeat(beat * 2);
      if (hasGrid) {
        const bar = profile.barIndex;
        if (bar != null && bar !== this._lastBarIndex) {
          if (this._lastBarIndex != null) this._onBar(beat * 2);
          this._lastBarIndex = bar;
        }
        const phrase = profile.phraseIndex;
        if (phrase != null && phrase !== this._lastPhraseIndex) {
          if (this._lastPhraseIndex != null) this._onPhrase(profile);
          this._lastPhraseIndex = phrase;
        }
      } else {
        if (this._lastBeat % BEATS_PER_BAR === 0) this._onBar(beat * 2);
        if (this._lastBeat % BEATS_PER_PHRASE === 0) this._onPhrase(profile);
      }
    }
  }

  // A seek moves the grid under the piece: the waves in flight belong to a
  // moment that no longer exists, and so do the last bar and phrase indices -
  // left in place, the first beat after the jump read as a bar change and
  // fired an inversion that no downbeat had asked for.
  _resync() {
    this._field.clear();
    this._extra.fill(0);
    this._lastBeat = null;
    this._lastBarIndex = null;
    this._lastPhraseIndex = null;
    this._invertUntil = -1;
    this._dropAt = -1e9;
  }

  // --- events ------------------------------------------------------------------

  _onBeat(beatHb) {
    const F = this._field;
    switch (this._pattern) {
      case 'ring':
        F.launch(this._pickOrigin(), beatHb);
        break;
      case 'image':
        // No fronts while the picture is up: a front over the picture sums
        // past 1 and goes white, and the cover was unreadable under it.
        if (!this._imageBar()) F.launch(this._pickOrigin(), beatHb);
        break;
      case 'rows':
        this._rowCursor = (this._rowCursor + 1) % F.rows;
        // rows count from the TOP, and the cursor descends
        this._rowLitHb[F.rows - 1 - this._rowCursor] = beatHb;
        break;
      case 'random': {
        const lit = this._randomLit;
        for (let i = 0; i < this._n; i++) lit[i] = Math.random() < RANDOM_SHARE ? 1 : 0;
        this._randomHb = beatHb;
        break;
      }
      default:
        break;
    }
  }

  // One bar in IMAGE_CYCLE_BARS shows the whole cover; the share grows with
  // progress, from one bar in four to three.
  _imageBar() {
    if (!this._imageLevel) return false;
    const imageBars = Math.round(1 + (IMAGE_CYCLE_BARS - 2) * this._progress);
    return this._barCount % IMAGE_CYCLE_BARS < imageBars;
  }

  _onBar(barHb) {
    this._barCount++;
    // INVERSION: every lit cell goes dark and every dark cell lights, for one
    // half-beat. Applied at the very end of the frame's evaluation, so it is
    // instantaneous whatever the field is doing.
    this._invertUntil = barHb + INVERT_HALF_BEATS;
    if (this._pattern === 'bloom') {
      const F = this._field;
      const count = Math.min(6, F.maxWaves);
      for (let k = 0; k < count; k++) {
        F.launch(F.towards((this._bloomTurn + k) % 6, BLOOM_DIST), barHb);
      }
      this._bloomTurn = (this._bloomTurn + count) % 6;
    }
  }

  _onPhrase(profile) {
    this._phraseCount++;
    const list = this._layout.patterns;
    if (!this._forcedPattern && list.length > 1) {
      let next = this._pattern;
      while (next === this._pattern) next = list[(Math.random() * list.length) | 0];
      this._setPattern(next);
    }
    if (this._pattern !== 'image') this._setFlatColor(this._phraseColor(profile));
  }

  _setPattern(name) {
    this._pattern = name;
    this._patternStartHb = this._hb;
    this._extra.fill(0);
    this._rowLitHb.fill(-1e9);
    this._randomLit.fill(0);
    if (name === 'image') {
      if (this._imageRGB) this._setImageColors();
    } else if (this._colorMode !== 'flat') {
      this._setFlatColor(this._flatHex != null ? this._flatHex : FALLBACK_VIVID[0]);
    }
  }

  // A wave origin that is NOT under the cover. The zone is the measured
  // overlay rectangle, in the same height units the shader uses: at the
  // wall's depth s = (x, y) / halfH, x being the flat position.
  _inSafeZone(i) {
    // R19: the centre of composition's zone, not the cover's
    const c = safeZoneUniforms.uStageCenter.value, h = safeZoneUniforms.uStageHalfSize.value;
    const feather = safeZoneUniforms.uSafeFeather.value;
    const sx = this._xFlat[i] / this._halfH, sy = this._y[i] / this._halfH;
    return Math.abs(sx - c.x) < h.x + feather && Math.abs(sy - c.y) < h.y + feather;
  }

  _pickOrigin() {
    for (let t = 0; t < RING_ORIGIN_TRIES; t++) {
      const i = (Math.random() * this._n) | 0;
      if (!this._inSafeZone(i)) return i;
    }
    return 0;
  }

  // --- the frame -----------------------------------------------------------

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;          // frozen: the attribute keeps its last frame
    this._time += dt;

    const director = profile.director || {};
    const live = director.hasReliableGrid && director.bands;
    this._lowBand = live ? clamp01(director.bands.low) : 0;
    this._highBand = live ? clamp01(director.bands.high) : 0;
    // bands.low -> trail thickness, 2 to 5 cells. Without a spectrum the
    // brief's default of 3.
    this._trail = live ? Math.round(TRAIL_MIN + (TRAIL_MAX - TRAIL_MIN) * this._lowBand) : TRAIL_DEFAULT;

    const palette = profile.artworkPalette;
    if (palette !== this._lastPalette) {
      this._lastPalette = palette;
      if (this._pattern !== 'image') this._setFlatColor(this._phraseColor(profile));
    }
    if (this._layout.image && profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      this._imageRGB = null; this._imageLevel = null;
      if (profile.artworkUrl) this._loadCover(profile.artworkUrl);
    }

    this._progress = clamp01(profile.progress || 0);
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._advanceClock(dt, profile, bpm);
    const hb = this._hb;
    const F = this._field;

    if (this._forceWave) {
      this._forceWave = false;
      F.launch(F.centre, Math.floor(hb));
    }
    const ev = director.nextEvent;
    if (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0
        && this._time - this._lastDropTime > 2.0) {
      this._lastDropTime = this._time;
      this._dropAt = hb;
    }

    // --- the field: every wave, by addition
    const sum = this._sum;
    sum.fill(0);
    F.evaluate(hb, this._trail, sum);

    // --- the pattern's own contribution
    const extra = this._extra;
    extra.fill(0);
    const n = this._n;
    switch (this._pattern) {
      case 'spiral': {
        // A snake winding out from the centre, one cell per half-beat.
        const path = F.spiral;
        const head = Math.floor(hb - this._patternStartHb);
        for (let j = 0; j <= SPIRAL_TAIL; j++) {
          const k = head - j;
          if (k < 0) break;
          extra[path[k % path.length]] += j === 0 ? 1 : Math.pow(0.78, j);
        }
        break;
      }
      case 'rows': {
        const rowLit = this._rowLitHb, rowOf = F.row;
        for (let i = 0; i < n; i++) {
          const age = hb - rowLit[rowOf[i]];
          if (age >= 0 && age < 6) extra[i] += Math.exp(-ROWS_DECAY * age);
        }
        break;
      }
      case 'random': {
        const lit = this._randomLit;
        const t = clamp01((hb - (this._randomHb || 0)) / 2);
        const level = 1 - (1 - RANDOM_HOLD) * t;
        for (let i = 0; i < n; i++) if (lit[i]) extra[i] += level;
        break;
      }
      case 'image': {
        // The picture bars show the whole cover, breathing over the bar. The
        // other bars are RING in the cover's colours: the fronts reveal the
        // picture cell by cell.
        if (this._imageBar()) {
          const breath = 0.85 + 0.15 * Math.sin(hb * Math.PI / 8);
          const lvl = this._imageLevel;
          for (let i = 0; i < n; i++) extra[i] += lvl[i] * breath;
        }
        break;
      }
      default:
        break;
    }

    // --- bands.high: rare sparks on unlit cells
    const spark = this._spark;
    const decay = Math.exp(-SPARK_DECAY * dt);
    for (let i = 0; i < n; i++) spark[i] *= decay;
    if (this._highBand > 0 && Math.random() < this._highBand * SPARK_RATE * dt) {
      const i = (Math.random() * n) | 0;
      if (sum[i] + extra[i] < LIT_THRESHOLD) spark[i] = SPARK_LEVEL;
    }

    // --- compose, invert, drop
    const out = this._litAttr.array;
    const inverted = hb < this._invertUntil;
    const dropAge = hb - this._dropAt;
    const inDrop = dropAge >= 0 && dropAge < DROP_FILL_HALF_BEATS + DROP_HOLD_HALF_BEATS + DROP_FADE_HALF_BEATS;
    let dropFront = -1, dropLevel = 0;
    if (inDrop) {
      if (dropAge < DROP_FILL_HALF_BEATS) {
        // a fill wave from the centre reaching the edges in one beat
        dropFront = Math.floor(dropAge / DROP_FILL_HALF_BEATS * (F.maxCentreDist + 1));
        dropLevel = 1;
      } else if (dropAge < DROP_FILL_HALF_BEATS + DROP_HOLD_HALF_BEATS) {
        dropFront = 255;
        dropLevel = 0.80 + 0.20 * Math.sin((dropAge - DROP_FILL_HALF_BEATS) * Math.PI / 2);
      } else {
        dropFront = 255;
        dropLevel = 1 - (dropAge - DROP_FILL_HALF_BEATS - DROP_HOLD_HALF_BEATS) / DROP_FADE_HALF_BEATS;
      }
    }
    const cd = F.centreDist;
    for (let i = 0; i < n; i++) {
      let v = sum[i] + extra[i];
      if (inverted) v = v > LIT_THRESHOLD ? 0 : INVERT_LEVEL;
      v += spark[i];
      if (inDrop && cd[i] <= dropFront) {
        // white: level 1 plus a full unit of whiteness
        const d = dropLevel * 2;
        if (d > v) v = d;
      }
      out[i] = v > 2 ? 2 : v;
    }
    this._litAttr.needsUpdate = true;
  }

  onTrackChange() {
    if (this._field) this._resync();
    this._barCount = 0;
  }

  dispose() {
    if (this._mesh) {
      this._mesh.onBeforeRender = null;
      if (this._scene) this._scene.remove(this._mesh);
      this._geo.dispose();
      this._material.dispose();
    }
    this._mesh = null;
    this._field = null;
    this._imageRGB = null;
    this._imageLevel = null;
  }
}

const _size = new THREE.Vector2();
const _tmpColor = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };
